import React from 'react'
import Icon from '@chegg-wt/core/components/Icon'
import { connect } from 'react-redux'
import ClientOnlyPortal from '@chegg-wt/core/components/ClientOnlyPortal'
import { AppState } from '@chegg-wt/core/state'
import { EXPORT_MODAL } from '@chegg-wt/core/state/modals/constants'
import { maybePluralizeWithNum } from '@chegg-wt/core/utils/language'
import { CITATION_LIST_NAME_LOWER_SINGLE } from '@chegg-wt/core/constants/citations'
import { useExportCitations } from '@chegg-wt/core/hooks/useExportCitations'
import { useMoveCitations } from '@chegg-wt/core/hooks/useMoveCitations'
import { useQuery } from '@apollo/client'
import { FETCH_PROJECTS } from '@chegg-wt/core/queries/Project'
import {
  FetchProjects,
  FetchProjectsVariables,
} from '@chegg-wt/core/queries/FetchProjects'
import { origins } from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import { Citation } from '@chegg-wt/core/types/redux-citation-api'
import { userClick } from '@chegg-wt/core/state/instrumentation/actions'
import { openModal, closeModal } from '@chegg-wt/core/state/modals/actions'
import { sortedCitationsSelector } from '@chegg-wt/core/state/bibliography/selectors'
import { currentStyleSelector } from '@chegg-wt/core/state/currentProject/selectors'
import { modalDataSelector } from '@chegg-wt/core/state/modals/selectors'
import { isProSelector, loggedInSelector } from '@chegg-wt/core/state/user/selectors'
import { GoogleDocModal } from '../GoogleDocModal'
import { client } from '../../../api/graphApi'
import {
  ExportModalProps,
  ExportModalStateProps,
  ExportModalDispatchProps,
  ExportType,
  Export,
  Move,
} from './types'
import { EXPORT_MODAL_CLOSE_BUTTON, EXPORT_MODAL_TITLE } from './testIds'
import * as Styled from './styled'
import { ExportTab } from './ExportTab'

export const ExportModal: React.FC<ExportModalProps> = ({
  selectedCitations,
  citations,
  isLoggedIn,
  isPro,
  exportTabIds,
  handleClose,
  openModal,
  currentStyle,
  handleComplete,
  userClick,
}) => {
  const { data: projectsData, error: projectsError } = useQuery<
    FetchProjects,
    FetchProjectsVariables
  >(FETCH_PROJECTS, {
    variables: {
      limit: 200,
    },
    skip: !isLoggedIn,
    ssr: false,
    fetchPolicy: 'cache-and-network',
    client,
  })

  // filter out the current project
  const currentProjectId = selectedCitations?.[0]?.projectId
  const projects = projectsData?.projects?.filter((p) => p.id !== currentProjectId)

  const { exportCitations, googleExport, errorCopy } = useExportCitations()
  const { moveCitations } = useMoveCitations()

  const handleExportCitations = (to: string, citations: Citation[]) => {
    exportCitations(currentStyle, to, citations, () => {
      if (to === 'docx') {
        handleClose()
      }
      const exportResult: Export = {
        type: ExportType.export,
        citations,
      }
      userClick({
        origin: origins.exportModuleExportCitationsClick,
        value: to === 'docx' ? 'Microsoft Word' : 'Google Docs',
      })
      handleComplete(exportResult)
    })
  }

  const handleMoveCitations = (projectId: string, citations: Citation[]) => {
    const projectName = projects.find((p) => p.id === projectId).name

    moveCitations(projectId, citations, () => {
      handleClose()
      const exportResult: Move = {
        type: ExportType.move,
        citations,
        projectName,
        projectId,
      }
      handleComplete(exportResult)
    })
  }

  const shouldShowExportModal = !googleExport?.shouldShowGoogleModal

  const isSelectAll =
    citations.length === selectedCitations.length || selectedCitations.length === 0
  const targetCitations = isSelectAll ? citations : selectedCitations

  const maybePluralizedCitation = maybePluralizeWithNum(
    targetCitations.length,
    'citation',
    'citations'
  )
  const title = isSelectAll
    ? `Export your ${CITATION_LIST_NAME_LOWER_SINGLE}`
    : `${maybePluralizedCitation} selected`

  return (
    <>
      <ClientOnlyPortal>
        <Styled.Modal
          show={shouldShowExportModal}
          onBackdropClick={handleClose}
          renderBackdrop={() => <Styled.DefaultBackdrop />}
        >
          <Styled.PositioningWrapper>
            <Styled.ModalContent fullMobileModal={false}>
              <Styled.HeaderContainer>
                <Styled.Header data-test={EXPORT_MODAL_TITLE}>{title}</Styled.Header>
                <Styled.CloseButton data-test={EXPORT_MODAL_CLOSE_BUTTON}>
                  <Icon name="clear" onClick={handleClose} />
                </Styled.CloseButton>
              </Styled.HeaderContainer>
              <ExportTab
                citations={targetCitations}
                handleClose={handleClose}
                exportTabIds={exportTabIds}
                projects={projects}
                exportCitations={handleExportCitations}
                moveCitations={handleMoveCitations}
                includeMove={isLoggedIn && !projectsError}
                isPro={isPro}
                openModal={openModal}
                handleComplete={handleComplete}
              />
            </Styled.ModalContent>
          </Styled.PositioningWrapper>
        </Styled.Modal>
      </ClientOnlyPortal>
      {googleExport && googleExport.shouldShowGoogleModal && (
        <GoogleDocModal
          isSignedIntoGoogle={googleExport.isSignedIntoGoogle}
          googleAuthResponse={googleExport.googleResponse}
          closeModal={() => {
            googleExport.close()
            handleClose()
          }}
          exportToGoogle={() => handleExportCitations('gdocs', targetCitations)}
          errorCopy={errorCopy}
        />
      )}
    </>
  )
}

const mapStateToProps = (state: AppState) => ({
  citations: sortedCitationsSelector(state),
  ...modalDataSelector(state),
  isLoggedIn: loggedInSelector(state),
  isPro: isProSelector(state),
  currentStyle: currentStyleSelector(state),
})

const mapDispatchToProps = {
  handleClose: () => closeModal(EXPORT_MODAL),
  userClick,
  openModal,
}

export default connect<ExportModalStateProps, ExportModalDispatchProps>(
  mapStateToProps,
  mapDispatchToProps
)(ExportModal)
